<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Diğer Satışlar</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Ara (ürün / seri / açıklama)"
        single-line
        hide-details
        clearable
        style="max-width: 300px"
      ></v-text-field>
      <v-btn color="primary" dark class="ml-3" @click="openSell">
        <v-icon left>point_of_sale</v-icon>
        Satış Yap
      </v-btn>
    </v-toolbar>

    <div class="caption grey--text px-3 pb-2">
      Sipariş dışı manuel satış. Envanterden düşer; iade edilince stok geri eklenir. Aktif satış toplamı:
      <strong>{{ formatMoney(activeTotal) }} ₺</strong>
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredLines"
      :loading="loading"
      :rows-per-page-items="[50, 100, 500, { text: 'Tümü', value: -1 }]"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td class="px-2">
          <v-chip small :color="typeColor(props.item.inventoryType)" text-color="white">
            {{ props.item.inventoryTypeName }}
          </v-chip>
        </td>
        <td class="px-2">{{ props.item.productName }}{{ variantSuffix(props.item) }}</td>
        <td class="px-2">{{ props.item.serialNumber || '-' }}</td>
        <td class="px-2 text-xs-right">{{ props.item.quantity }}</td>
        <td class="px-2 text-xs-right">{{ formatMoney(props.item.amount) }} ₺</td>
        <td class="px-2">{{ formatDate(props.item.saleDate) }}</td>
        <td class="px-2">{{ props.item.description || '-' }}</td>
        <td class="px-2">
          <v-chip small :color="props.item.status === 0 ? 'green' : 'grey'" text-color="white">
            {{ props.item.statusName }}
          </v-chip>
        </td>
        <td class="px-2">
          <v-btn v-if="props.item.status === 0" small flat color="red darken-1" @click="openRefund(props.item)">
            <v-icon small left>undo</v-icon>
            İade
          </v-btn>
        </td>
      </template>
      <template v-slot:no-data>
        <div class="text-xs-center pa-4 grey--text">Henüz manuel satış yok.</div>
      </template>
    </v-data-table>

    <!-- ========== SATIŞ YAP DIALOG ========== -->
    <v-dialog v-model="sellDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">point_of_sale</v-icon>
          Manuel Satış
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field v-model="sellForm.amount" label="Tutar (₺)" placeholder="Örn. 12500" prepend-icon="payments"></v-text-field>
          <v-textarea v-model="sellForm.description" label="Açıklama" rows="2" auto-grow></v-textarea>

          <v-tabs v-model="sellTab" grow class="mb-2">
            <v-tab>Ziynet / Külçe / Diğer</v-tab>
            <v-tab>Takı</v-tab>
          </v-tabs>

          <div v-if="sellTab === 0">
            <v-select
              v-model="sellForm.inventoryItemId"
              :items="adetItems"
              item-text="label"
              item-value="id"
              label="Ürün"
            ></v-select>
            <div v-if="selectedInventoryItem" class="caption grey--text mb-2">
              Ana envanterde boşta: <strong>{{ availableOf(selectedInventoryItem) }}</strong> adet
            </div>
            <v-text-field v-model.number="sellForm.quantity" label="Adet" type="number" min="1"></v-text-field>
          </div>

          <div v-else>
            <v-text-field
              v-model="sellForm.barcode"
              label="Takı Barkodu / Seri No"
              prepend-icon="qr_code_scanner"
              hint="Sertifikadaki barkodu okutun veya yazın"
              persistent-hint
              @keyup.enter="saveSell"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="sellDialog = false">İptal</v-btn>
          <v-btn color="primary" dark :loading="sellSaving" @click="saveSell">Satış Yap</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== İADE DIALOG (adet) ========== -->
    <v-dialog v-model="refundDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline red darken-1 white--text">
          <v-icon left color="white">undo</v-icon>
          Satışı İade Et
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="mb-2">{{ refundTarget && refundTarget.productName }}{{ variantSuffix(refundTarget) }}</div>
          <v-text-field
            v-model.number="refundQuantity"
            label="İade edilecek adet"
            type="number"
            min="1"
            :max="refundTarget ? refundTarget.quantity : 1"
          ></v-text-field>
          <div class="caption grey--text">Satılan: {{ refundTarget ? refundTarget.quantity : 0 }} adet</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="refundDialog = false">İptal</v-btn>
          <v-btn color="red darken-1" dark :loading="refundSaving" @click="confirmRefund">İade Et</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "DigerSatislar",
  data: () => ({
    loading: false,
    lines: [],
    inventoryItems: [],
    zimmetLines: [],
    search: "",
    headers: [
      { text: "Tip", value: "inventoryTypeName" },
      { text: "Ürün", value: "productName" },
      { text: "Seri No", value: "serialNumber" },
      { text: "Adet", value: "quantity", align: "right" },
      { text: "Tutar", value: "amount", align: "right" },
      { text: "Tarih", value: "saleDate" },
      { text: "Açıklama", value: "description" },
      { text: "Durum", value: "status" },
      { text: "", value: "actions", sortable: false },
    ],

    sellDialog: false,
    sellSaving: false,
    sellTab: 0,
    sellForm: { amount: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" },

    refundDialog: false,
    refundSaving: false,
    refundTarget: null,
    refundQuantity: 1,
  }),

  computed: {
    adetItems() {
      return this.inventoryItems
        .filter((i) => i.inventoryType !== 0)
        .map((i) => ({
          ...i,
          label: `${i.inventoryTypeName} - ${i.productName}${this.variantSuffix(i)} (${i.weight} gr, ${i.purityLabel}) — boşta ${this.availableOf(i)}`,
        }));
    },
    selectedInventoryItem() {
      return this.inventoryItems.find((i) => i.id === this.sellForm.inventoryItemId) || null;
    },
    activeTotal() {
      return this.lines.filter((l) => l.status === 0).reduce((s, l) => s + (Number(l.amount) || 0), 0);
    },
    filteredLines() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      if (!q) return this.lines;
      return this.lines.filter((l) =>
        [l.productName, l.serialNumber, l.inventoryTypeName, l.description]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q))
      );
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    variantSuffix(i) {
      if (!i) return "";
      const parts = [i.kulp, i.dateType].filter(Boolean);
      return parts.length ? ` [${parts.join(" / ")}]` : "";
    },
    typeColor(t) {
      if (t === 0) return "teal";
      if (t === 1) return "amber darken-2";
      return "blue-grey darken-1";
    },
    formatMoney(v) {
      if (v == null) return "0,00";
      return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("tr-TR");
    },
    parseNum(s) {
      if (s == null || s === "") return null;
      const n = parseFloat(String(s).trim().replace(/\./g, "").replace(",", "."));
      return Number.isNaN(n) ? null : n;
    },
    availableOf(item) {
      if (!item) return 0;
      const assigned = this.zimmetLines
        .filter((l) => l.inventoryItemId === item.id)
        .reduce((s, l) => s + (l.quantity || 0), 0);
      return item.quantity - assigned;
    },

    async loadAll() {
      this.loading = true;
      try {
        const [linesRes, itemsRes, zimmetRes] = await Promise.all([
          this.$store.dispatch("inventory/getOtherSales"),
          this.$store.dispatch("inventory/getInventoryItems"),
          this.$store.dispatch("inventory/getAllZimmets"),
        ]);
        this.lines = linesRes || [];
        this.inventoryItems = itemsRes || [];
        this.zimmetLines = zimmetRes || [];
      } catch (e) {
        this.notify("Satış verileri yüklenemedi: " + this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },

    openSell() {
      this.sellForm = { amount: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" };
      this.sellTab = 0;
      this.sellDialog = true;
    },

    async saveSell() {
      const amount = this.parseNum(this.sellForm.amount);
      if (amount == null || amount < 0) {
        this.notify("Geçerli bir tutar girin.");
        return;
      }
      let dto;
      if (this.sellTab === 1) {
        const code = (this.sellForm.barcode || "").trim().replace(/\*/g, "-"); // okuyucu "-"yi "*" gönderebilir
        if (!code) { this.notify("Takı barkodu okutun."); return; }
        dto = { amount, description: this.sellForm.description, barcode: code };
      } else {
        if (!this.sellForm.inventoryItemId) { this.notify("Ürün seçin."); return; }
        if (!this.sellForm.quantity || this.sellForm.quantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
        dto = {
          amount,
          description: this.sellForm.description,
          inventoryItemId: this.sellForm.inventoryItemId,
          quantity: this.sellForm.quantity,
        };
      }
      this.sellSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/otherSaleSell", dto);
        if (res && res.success === false) { this.notify(res.message); return; }
        this.sellDialog = false;
        this.notify((res && res.message) || "Satış kaydedildi.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.sellSaving = false;
      }
    },

    openRefund(line) {
      this.refundTarget = line;
      if (line.inventoryType === 0) {
        this.doRefund(line, 0);
        return;
      }
      this.refundQuantity = line.quantity;
      this.refundDialog = true;
    },

    async confirmRefund() {
      if (!this.refundTarget) return;
      if (!this.refundQuantity || this.refundQuantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
      await this.doRefund(this.refundTarget, this.refundQuantity);
      this.refundDialog = false;
    },

    async doRefund(line, quantity) {
      this.refundSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/otherSaleRefund", { saleId: line.id, quantity });
        if (res && res.success === false) { this.notify(res.message); return; }
        this.notify((res && res.message) || "İade edildi.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.refundSaving = false;
      }
    },

    errMsg(e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Bilinmeyen hata";
    },
    notify(msg) {
      this.$store.state.dialog = true;
      this.$store.state.message = msg;
    },
  },
};
</script>
